import { useMemo, useState } from 'react';
import { useAuthStore } from '../store/auth';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input } from '@lms/ui';
import { apiClient, updateApiClientToken } from '../lib/api-client';
import { ArrowLeft, ArrowRight, ClipboardCheck, Send } from 'lucide-react';
import { loginCopy, brandingCopy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage } from '../lib/LanguageContext';

interface LoginFormInput {
  identifier: string;
  password?: string;
}

export function Login() {
  const navigate = useNavigate();
  const setAuth = useAuthStore(state => state.setAuth);
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [role, setRole] = useState<'admin' | 'pengajar'>('admin');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { language, setLanguage, t } = useLanguage();

  const copy = loginCopy[role];
  const authSchema = useMemo(
    () =>
      isForgotMode
        ? z.object({
            identifier: z.string().email(t(copy.emailInvalid)),
            password: z.string().optional(),
          })
        : z.object({
            identifier: z.string().min(1, t(copy.identifierRequired)),
            password: z.string().min(1, t(copy.passwordRequired)),
          }),
    [copy, isForgotMode, t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormInput>({
    resolver: zodResolver(authSchema),
  });

  const onSubmit = async (data: LoginFormInput) => {
    setMessage(null);
    try {
      if (isForgotMode) {
        await apiClient.auth.requestPasswordReset({
          email: data.identifier,
          portal: 'ADMIN',
        });
        setMessage({
          type: 'success',
          text: t(copy.resetSuccess),
        });
        return;
      }

      if (!data.password) return;
      const session = await apiClient.auth.login({
        identifier: data.identifier,
        password: data.password,
      });
      const roles = session.user.roles ?? [];
      if (!roles.some(roleName => roleName === 'ADMIN' || roleName === 'TEACHER')) {
        throw new Error(t(copy.authFailed));
      }
      updateApiClientToken(session.accessToken);
      setAuth(session.accessToken, {
        ...session.user,
        roles,
      });
      void navigate('/');
    } catch (e: unknown) {
      const error = e as Error;
      setMessage({
        type: 'error',
        text: error.message || t(copy.authFailed),
      });
    }
  };

  return (
    <main className="min-h-screen bg-surface p-3 font-body text-on-surface sm:p-5 lg:p-6">
      <div className="mx-auto grid min-h-[calc(100vh-1.5rem)] max-w-[1440px] overflow-hidden rounded-[2rem] bg-surface-container-low md:min-h-[calc(100vh-2.5rem)] md:grid-cols-[minmax(0,1.04fr)_minmax(25rem,0.96fr)] lg:min-h-[calc(100vh-3rem)]">
        {/* Sisi Kiri — Editorial Branding Plane */}
        <section className="flex min-h-[18rem] flex-col justify-between overflow-hidden bg-primary px-6 py-7 text-on-primary relative sm:px-10 sm:py-10 md:min-h-0 md:px-12 md:py-12 lg:px-16 lg:py-16">
          <div className="absolute inset-0 bg-gradient-to-br from-primary to-primary-container opacity-95" />
          <div className="relative z-10 flex items-center gap-3">
            <img
              alt="Tarbiyah Sunnah Learning Brand Logo"
              className="h-20 w-auto object-contain brightness-0 invert sm:h-24"
              src="/logo.png"
            />
            <span className="h-8 w-px bg-on-primary/25" aria-hidden="true" />
            <ClipboardCheck className="h-5 w-5 text-secondary" aria-hidden="true" />
          </div>
          <div className="relative z-10 max-w-lg py-8 md:py-12">
            <h1 className="min-w-0 break-words font-headline font-extrabold text-6xl lg:text-7xl tracking-tighter leading-[1] mb-8">
              <Bilingual {...brandingCopy.title} stack forceBilingual />
            </h1>
            <div className="font-body text-base lg:text-lg text-on-primary-variant/90 leading-relaxed mb-12">
              <Bilingual {...brandingCopy.tagline} forceBilingual />
            </div>
            <div className="flex gap-4 items-center">
              <div className="h-[2px] w-12 bg-on-primary opacity-30" />
              <div className="font-headline font-bold text-xs uppercase tracking-widest text-on-primary opacity-80 m-0">
                <Bilingual {...brandingCopy.motto} forceBilingual />
              </div>
            </div>
          </div>

          {/* Heritage Decorative Elements */}
          <div className="absolute -bottom-32 -right-32 w-[600px] h-[600px] bg-primary-container rounded-full opacity-20 blur-[120px]" />
          <div className="absolute top-1/2 -left-48 w-96 h-96 bg-secondary rounded-full opacity-10 blur-[100px]" />
        </section>

        {/* Sisi Kanan — Interface Plane */}
        <section className="flex items-center justify-center bg-surface-container-lowest px-5 py-9 sm:px-10 sm:py-12 md:px-12 lg:px-16 relative">
          {/* Language Switcher */}
          <div className="absolute right-5 top-5 flex items-center rounded-lg bg-surface-container p-1 shadow-level1 sm:right-8 sm:top-8">
            <button
              type="button"
              onClick={() => setLanguage('id')}
              aria-label="Bahasa Indonesia"
              className={`px-3 py-1.5 text-[11px] font-bold rounded-md transition-colors ${
                language === 'id'
                  ? 'bg-primary text-on-primary shadow-level1'
                  : 'text-on-surface-variant hover:bg-surface-container-high'
              }`}
            >
              ID
            </button>
            <button
              type="button"
              onClick={() => setLanguage('ar')}
              aria-label="اللغة العربية"
              className={`px-3 py-1.5 text-[11px] font-bold rounded-md transition-colors ${
                language === 'ar'
                  ? 'bg-primary text-on-primary shadow-level1'
                  : 'text-on-surface-variant hover:bg-surface-container-high'
              }`}
            >
              AR
            </button>
          </div>

          <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-4 duration-700 pt-10 sm:pt-8">
            {/* Role Toggle */}
            <div className="mb-8 flex gap-2 rounded-2xl bg-surface-container-low p-1.5">
              {(['admin' /*, 'pengajar'*/] as const).map(r => (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  type="button"
                  className={`flex-1 py-2.5 px-4 rounded-xl font-headline font-bold text-sm transition-colors ${
                    role === r
                      ? 'bg-surface-container-lowest text-on-surface shadow-level1'
                      : 'text-on-surface-variant hover:bg-surface-container-high'
                  }`}
                >
                  {r === 'admin' ? 'Admin' : 'Pengajar'}
                </button>
              ))}
            </div>

            <div className="mb-9">
              {isForgotMode ? (
                <h2 className="font-headline font-bold text-3xl text-primary tracking-[-0.03em] mb-3 sm:text-4xl">
                  <Bilingual {...copy.resetTitle} />
                </h2>
              ) : (
                <Bilingual
                  {...copy.salutation}
                  stack
                  size="large"
                  className="font-headline font-bold text-3xl text-primary tracking-[-0.03em] mb-3 sm:text-4xl"
                />
              )}
              <div className="text-on-surface-variant font-body text-sm leading-6 sm:text-base">
                {isForgotMode ? (
                  <Bilingual {...copy.resetSubtitle} />
                ) : (
                  <Bilingual {...copy.subtitle} />
                )}
              </div>
            </div>

            {message && (
              <div
                role="status"
                className={`mb-8 p-4 rounded-xl text-sm font-medium shadow-level1 ${
                  message.type === 'success'
                    ? 'bg-primary/10 text-primary'
                    : 'bg-error/10 text-error'
                }`}
              >
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <Input
                label={
                  isForgotMode ? (
                    <Bilingual {...copy.emailLabel} />
                  ) : (
                    <Bilingual {...copy.identifierLabel} />
                  )
                }
                type={isForgotMode ? 'email' : 'text'}
                autoComplete={isForgotMode ? 'email' : 'username'}
                placeholder={
                  role === 'admin'
                    ? 'email, NIP, atau No. HP admin'
                    : 'email, NIP, atau No. HP pengajar'
                }
                {...register('identifier')}
                error={errors.identifier?.message}
              />

              {!isForgotMode && (
                <div>
                  <Input
                    label={<Bilingual {...copy.passwordLabel} />}
                    type="password"
                    placeholder="••••••••"
                    {...register('password')}
                    error={errors.password?.message}
                  />
                  <div className="mt-3 flex justify-end px-1">
                    <button
                      type="button"
                      onClick={() => setIsForgotMode(true)}
                      className="bg-transparent text-[11px] font-bold text-primary hover:text-secondary transition-colors uppercase tracking-widest active:scale-95"
                    >
                      <Bilingual {...copy.forgotLabel} size="small" />
                    </button>
                  </div>
                </div>
              )}

              {!isForgotMode && (
                <div className="flex items-center ml-1">
                  <input
                    id="remember"
                    type="checkbox"
                    className="w-4 h-4 text-primary bg-surface-container border-none rounded focus:ring-primary focus:ring-offset-surface-container-lowest"
                  />
                  <label
                    htmlFor="remember"
                    className="ml-3 text-sm text-on-surface-variant font-medium select-none"
                  >
                    <Bilingual {...copy.rememberLabel} />
                  </label>
                </div>
              )}

              <div className="pt-4 space-y-4">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  isLoading={isSubmitting}
                  className="w-full rounded-lg flex items-center justify-center gap-3 group active:scale-95"
                >
                  <Bilingual {...(isForgotMode ? copy.sendLink : copy.submitLabel)} />
                  {isForgotMode ? (
                    <Send className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                  ) : (
                    <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                  )}
                </Button>

                {isForgotMode && (
                  <button
                    type="button"
                    onClick={() => setIsForgotMode(false)}
                    className="flex w-full items-center justify-center gap-2 bg-transparent text-sm font-bold text-on-surface-variant hover:text-primary transition-colors active:scale-95"
                  >
                    <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                    <Bilingual {...copy.backToLogin} />
                  </button>
                )}
              </div>
            </form>
          </div>
        </section>
      </div>
    </main>
  );
}
